import type { ShopifyThemeProjectEntitlementRejectedModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { SplitModal } from "@replo/design-system/blocks/modal/SplitModal";
import { Button } from "@replo/design-system/components/button/Button";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { showSubscriptionUpdateSuccessToast } from "@v2/features/billing/billingToasts";
import useInitializeSubscriptionUpdateMutation from "@v2/features/billing/hooks/useInitializeSubscriptionUpdateMutation";
import { getProratedCharge } from "@v2/features/billing/planPriceUtils";
import {
  getCountOfAdditionalProjectsInSubscription,
  getCountOfAdditionalPublishedElementBundlesInSubscription,
  getCountOfAdditionalSitesInSubscription,
} from "@v2/features/billing/shared/sharedBillingUtils";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useSubscriptionDetails } from "@v2/hooks/subscription";
import { useModal } from "@v2/store/modalStore";
import { billingRouter } from "@v2/trpc/routes/billing";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { getProjectLabels } from "@v2/utils/project-labels";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";
import { formatPrice } from "replo-utils/lib/math";
import {
  ADDITIONAL_SHOPIFY_THEME_PROJECT_PRICE_PER_MONTH,
  AddOn,
} from "schemas/addOns";

const ShopifyThemeProjectEntitlementRejectedModal: React.FC<
  ShopifyThemeProjectEntitlementRejectedModalProps
> = ({ explanationMessage }) => {
  const workspace = useCurrentWorkspace();
  const modal = useModal();
  const { data: activeSubscription } = useSubscriptionDetails();

  const { data: shopifyThemeProjectsUsage } =
    workspaceRouter.getShopifyThemeProjectsUsage.useQuery(
      workspace?.id ?? skipToken,
    );

  const { data: nextBillingDate } =
    billingRouter.getNextCorePlanBillingDate.useQuery(
      workspace?.id && activeSubscription?.id
        ? {
            workspaceId: workspace.id,
            subscriptionId: activeSubscription.id,
          }
        : skipToken,
    );

  const {
    initializeSubscriptionUpdate,
    isLoading: isLoadingSubscriptionUpdate,
  } = useInitializeSubscriptionUpdateMutation();

  const closeModal = () => {
    modal.closeModal({ type: "shopifyThemeProjectEntitlementRejectedModal" });
  };

  if (
    !workspace ||
    !shopifyThemeProjectsUsage ||
    shopifyThemeProjectsUsage.maximum === null // If they have unlimited projects, we don't need to show this modal
  ) {
    return null;
  }

  const labels = getProjectLabels("shopify");

  let headerText: string;
  let content: React.ReactNode;

  if (!activeSubscription) {
    headerText = "Upgrade to a Paid Plan Required";
    content = (
      <FreeUserUpgradeRequiredContent
        explanationMessage={explanationMessage}
        onClose={closeModal}
        workspaceId={workspace.id}
      />
    );
  } else {
    if (!nextBillingDate) {
      return null;
    }

    const isFromEntitlement = shopifyThemeProjectsUsage.isFromEntitlement;

    const activeSubscriptionIsYearly =
      activeSubscription.billingInterval === "yearly";

    const additionalProjectFullPeriodPrice = activeSubscriptionIsYearly
      ? ADDITIONAL_SHOPIFY_THEME_PROJECT_PRICE_PER_MONTH * 12
      : ADDITIONAL_SHOPIFY_THEME_PROJECT_PRICE_PER_MONTH;

    const formattedAdditionalProjectPrice = activeSubscriptionIsYearly
      ? `${formatPrice(additionalProjectFullPeriodPrice)}/year`
      : `${formatPrice(additionalProjectFullPeriodPrice)}/month`;

    const formattedNextBillingDate = new Date(
      nextBillingDate,
    ).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    const proratedCharge = getProratedCharge({
      billingInterval: activeSubscription.billingInterval,
      nextBillingDate: new Date(nextBillingDate),
      fullPeriodPrice: additionalProjectFullPeriodPrice,
    });

    const handleConfirm = async () => {
      const currentAdditionalProjects =
        getCountOfAdditionalProjectsInSubscription(activeSubscription);

      const currentAdditionalSites =
        getCountOfAdditionalSitesInSubscription(activeSubscription);

      const newAdditionalProjects = currentAdditionalProjects + 1;

      const currentAdditionalPublishedElementBundles =
        getCountOfAdditionalPublishedElementBundlesInSubscription(
          activeSubscription,
        );

      analytics.logEvent(
        "billing.shopifyThemeProjectEntitlementRejectedModal.purchasedAddOn",
        {
          currentPlan: activeSubscription.billingTier,
          newAdditionalShopifyThemeProjects: newAdditionalProjects,
        },
      );

      const addOns = AddOn.buildFromCounts({
        additionalProjects: newAdditionalProjects,
        additionalPublishedElementBundles:
          currentAdditionalPublishedElementBundles,
        additionalSites: currentAdditionalSites,
      });

      await initializeSubscriptionUpdate({
        workspaceId: workspace.id,
        billingTier: activeSubscription.billingTier,
        billingInterval: activeSubscription.billingInterval,
        addOns,
        source: "direct.billingDashboard",
      });
      showSubscriptionUpdateSuccessToast();
      closeModal();
    };

    if (activeSubscription.isManagedByReplo || isFromEntitlement) {
      headerText = `Add active ${labels.plural}`;
    } else {
      headerText = `Add an active ${labels.singular}`;
    }

    if (activeSubscription.isManagedByReplo) {
      content = (
        <PlanManagedByReploContent
          explanationMessage={explanationMessage}
          onClose={closeModal}
          labels={labels}
        />
      );
    } else if (isFromEntitlement) {
      content = (
        <IsFromEntitlementContent
          explanationMessage={explanationMessage}
          onClose={closeModal}
          labels={labels}
        />
      );
    } else {
      content = (
        <AddShopContent
          explanationMessage={explanationMessage}
          formattedAdditionalProjectPrice={formattedAdditionalProjectPrice}
          formattedNextBillingDate={formattedNextBillingDate}
          proratedCharge={proratedCharge}
          newTotalProjects={shopifyThemeProjectsUsage.maximum + 1}
          isLoading={isLoadingSubscriptionUpdate}
          onConfirm={() => void handleConfirm()}
          onClose={closeModal}
          labels={labels}
        />
      );
    }
  }

  return (
    <SplitModal
      open={true}
      title={headerText}
      media={
        <img
          src="/images/billing/shop-modal-graphic.png"
          alt=""
          className="h-full w-full object-cover"
        />
      }
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
    >
      {content}
    </SplitModal>
  );
};

type Labels = ReturnType<typeof getProjectLabels>;

const AddShopContent = ({
  explanationMessage,
  formattedAdditionalProjectPrice,
  formattedNextBillingDate,
  proratedCharge,
  newTotalProjects,
  isLoading,
  onConfirm,
  onClose,
  labels,
}: {
  explanationMessage: string;
  formattedAdditionalProjectPrice: string;
  formattedNextBillingDate: string;
  proratedCharge: number;
  newTotalProjects: number;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
  labels: Labels;
}) => {
  return (
    <div className="flex flex-col gap-8 justify-between">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>
          Adding 1 {labels.singular} will increase your Workspace bill by{" "}
          <span className="font-bold">{formattedAdditionalProjectPrice}</span>{" "}
          starting {formattedNextBillingDate}, bringing your total to{" "}
          <span className="font-bold">
            {newTotalProjects} {labels.plural}.
          </span>
          <br />
          <br /> A prorated charge of{" "}
          <span className="font-bold">{formatPrice(proratedCharge)}</span> will
          be applied today to your card on file.
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <Button
          variant="primary"
          size="base"
          onClick={onConfirm}
          loading={isLoading}
        >
          Confirm
        </Button>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const FreeUserUpgradeRequiredContent = ({
  explanationMessage,
  onClose,
  workspaceId,
}: {
  explanationMessage: string;
  onClose: () => void;
  workspaceId: string;
}) => {
  const navigate = useNavigate();

  const handleNavigateToBilling = () => {
    onClose();
    navigate(generatePath(routes.workspace.settings.billing, { workspaceId }));
  };

  return (
    <div className="flex flex-col gap-8 justify-between">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>You'll need to upgrade to a paid plan to continue.</div>
      </div>
      <div className="flex flex-col gap-3">
        <Button variant="primary" size="base" onClick={handleNavigateToBilling}>
          See Plan Options
        </Button>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const PlanManagedByReploContent = ({
  explanationMessage,
  onClose,
  labels,
}: {
  explanationMessage: string;
  onClose: () => void;
  labels: Labels;
}) => {
  return (
    <div className="flex flex-col gap-8 justify-between">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>
          Since your plan is managed by Replo, please reach out to{" "}
          <span className="font-bold">billing@replo.app</span> to add{" "}
          {labels.plural}. Our team will help you adjust your plan to best suit
          your needs!
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <a
          href={`mailto:billing@replo.app?subject=Managed Plan - Add ${labels.pluralCapitalized}`}
        >
          <Button variant="primary" size="base">
            Contact Us
          </Button>
        </a>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const IsFromEntitlementContent = ({
  explanationMessage,
  onClose,
  labels,
}: {
  explanationMessage: string;
  onClose: () => void;
  labels: Labels;
}) => {
  return (
    <div className="flex flex-col gap-8 justify-between">
      <div className="text-sm font-normal">
        {explanationMessage}
        <br />
        <br />
        <div>
          Your plan's maximum number of {labels.plural} has been set manually by
          the Replo team. This could be due to a custom request or a plan
          grandfathered in from our older pricing structure.
          <br />
          <br />
          Please reach out to{" "}
          <span className="font-bold">billing@replo.app</span> to add{" "}
          {labels.plural}. Our team will help you adjust your plan to best suit
          your needs!
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <a
          href={`mailto:billing@replo.app?subject=Custom Plan - Add ${labels.pluralCapitalized}`}
        >
          <Button variant="primary" size="base">
            Contact Us
          </Button>
        </a>
        <Button variant="secondary" size="base" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default ShopifyThemeProjectEntitlementRejectedModal;
